import { hasAdminAccessFeature } from '../../util/accessControl'
import { selectAllFeatures, selectUserSubscriptionPlans, selectUserSubscriptionPlansData } from '../../selectors/user'
import type { UserDetailType } from '../../reducer/user.reducer'
import type { RootState } from '../../store'
import store from '../../store'
import { PremiumFeature } from '../../proto/generated/subscription'

/** Compile-time gate — safe to read on first paint. */
export const isAiAssistantFeatureFlagOn =
    import.meta.env.VITE_AI_ASSISTANT === 'true' ||
    /** @deprecated legacy screener-only flag; same as VITE_AI_ASSISTANT */
    import.meta.env.VITE_SCREENER_AI_V2 === 'true'

const AI_ASSISTANT_ADMIN_CACHE_KEY = 'tick2trade-ai-assistant-admin-cache'

function readAdminCache(): boolean | null {
    if (typeof window === 'undefined') return null
    const raw = window.sessionStorage.getItem(AI_ASSISTANT_ADMIN_CACHE_KEY)
    if (raw === 'true') return true
    if (raw === 'false') return false
    return null
}

export function cacheAiAssistantAdminAccess(hasAccess: boolean): void {
    if (typeof window === 'undefined') return
    window.sessionStorage.setItem(AI_ASSISTANT_ADMIN_CACHE_KEY, String(hasAccess))
}

function resolveUserFeatures(state: RootState): PremiumFeature[] | undefined {
    const planIds = selectUserSubscriptionPlans(state)
    const plansById = selectUserSubscriptionPlansData(state)
    if (!planIds?.length || !plansById) return undefined
    return planIds.flatMap((plan) => plansById[plan] ?? [])
}

function hasAdminAccessFromStore(): boolean {
    const state = store.getState()
    return hasAdminAccessFeature(selectAllFeatures(state), resolveUserFeatures(state))
}

/** Full gate for opening / using the assistant (requires loaded profile + ADMIN_ACCESS). */
export function isAiAssistantEnabled(_user?: UserDetailType | undefined | null): boolean {
    if (!isAiAssistantFeatureFlagOn) return false
    return hasAdminAccessFromStore()
}

/**
 * Sidebar visibility — ADMIN_ACCESS only; never show the icon until access is confirmed.
 */
export function shouldShowAiAssistantInSidebar(user: UserDetailType | undefined | null): boolean {
    if (!isAiAssistantFeatureFlagOn) return false
    if (user?.currentUser) {
        const allowed = hasAdminAccessFromStore()
        cacheAiAssistantAdminAccess(allowed)
        return allowed
    }
    const cached = readAdminCache()
    if (cached !== null) return cached
    return false
}
